import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Check } from 'lucide-react';
import { PublicLayout } from '../layout/PublicLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  PLANS,
  estCostPerMeeting,
  estMeetings,
  estReplies,
  formatCredits,
  paymentLinksReady,
  usd,
  type Plan,
} from '@/components/monetization/plans';
import { handToStripe, stripeCheckoutUrl } from '@/components/monetization/stripeNavigation';
import { posthogCapture } from '@/config/posthog';
import { rememberPostLoginPath } from '@/utils/postLoginPath';
import { AUTH_USER_COOKIE, getAuthJsonCookie } from '@/utils/authCookies';
import { useSEO } from '@/hooks/useSEO';

/**
 * PricingPage — THE PUBLIC PRICING SURFACE (P091 D-084, the named task that decision left open).
 *
 * ── WHO THIS IS FOR (RULES_MASTER §2.11 backward-derivation — the served party, not the mechanism) ──
 * The reader is **a founder who has not signed up**: Maya (the equity-fundraising wedge) wearing the
 * **FTUE posture**, which is always-on and unweighted. She arrives suspicious by default, has
 * overpromise fatigue ("claims of magic are read as lies"), fears losing control of what goes out in
 * her name, and — FTUE W1/W6 — trusts small true visible proof, never a big promise. Maya's own file
 * says she buys the OUTCOME ("my raise is being run properly"), never "an email tool", and her
 * expectation is that the meeting is the output.
 *
 * WHAT THAT FORCES ON THIS PAGE, each one checkable:
 *   - **W6 no overpromise / W1 prove it's real.** Every figure here is DERIVED from `plans.ts` (the
 *     one stone, the same file the in-app dialog renders), and everything the research bands produced
 *     travels with the label **`est.`** — the estimates are the promise's softest half and are never
 *     presented as a commitment. Nothing is typed twice, so nothing can disagree.
 *   - **W5 the fences are visible.** The price, the per-reachout rate, the cancellation rail (Stripe,
 *     month-to-month) and what a credit is are all stated before anyone is asked to pay.
 *   - **W11 never make me feel like a problem.** The vocabulary is positive and true; a state the page
 *     cannot vouch for is not dressed up (if a checkout link is missing it says so instead of
 *     rendering a dead button — RULES_FE_QA critical walk #10).
 *
 * ── WHY THE CTA GOES TO SIGN-UP RATHER THAN TO STRIPE (D-084 fork A, D-083's gate) ──────────────────
 * The backend refuses to grant a payment it cannot attribute (`_grant_for_payment` resolves the payer
 * as `client_reference_id` -> uid, then the billing email). A signed-out buyer's credits would land
 * nowhere he can see — money taken for nothing, the one outcome worse than a declined purchase. So
 * **no identity, no checkout**: the plan he chose is remembered (`rememberPostLoginPath`) and carried
 * through sign-up, and he comes back to THIS surface with that plan selected. The one-popup law (D-062
 * / `MonetizationProvider`) is respected because this surface is not a second dialog — it is the
 * public page that hands him to the same single rail.
 */

/** The plan named in `?plan=`, when it names one we actually sell. */
function chosenPlanKey(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('plan');
  } catch {
    return null;
  }
}

/** Is there a session? Read from the same JS-readable cookie the auth guard writes (D-006) — no call,
 *  and no assumption about a hook being mounted on a public page. */
function hasSession(): boolean {
  try {
    return Boolean(getAuthJsonCookie(AUTH_USER_COOKIE));
  } catch {
    return false;
  }
}

const PlanCard = ({
  plan,
  chosen,
  notice,
  onPress,
}: {
  plan: Plan;
  chosen: boolean;
  notice: string | null;
  onPress: (plan: Plan) => void;
}) => {
  const replies = estReplies(plan);
  const meetings = estMeetings(plan);
  const perMeeting = estCostPerMeeting(plan);

  return (
    <div
      className={`relative flex flex-col rounded-2xl border bg-white p-6 dark:bg-slate-900 ${
        chosen
          ? 'border-cyan-500 ring-2 ring-cyan-500'
          : plan.popular
            ? 'border-cyan-200 dark:border-cyan-800'
            : 'border-slate-200 dark:border-slate-700'
      }`}
    >
      {(chosen || plan.popular) && (
        <span className="absolute -top-3 left-6 rounded-full bg-cyan-600 px-3 py-1 text-xs font-semibold text-white">
          {chosen ? 'Your choice' : 'Most chosen'}
        </span>
      )}

      <h2 className="text-xl font-bold text-slate-900 dark:text-white">{plan.name}</h2>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{plan.tagline}</p>

      <p className="mt-5 flex items-baseline gap-1">
        <span className="text-4xl font-bold text-slate-900 dark:text-white">
          {usd(plan.monthlyPriceUsd)}
        </span>
        <span className="text-sm text-slate-500 dark:text-slate-400">a month</span>
      </p>

      {/* WHAT HE BUYS, in the platform's one currency, and what it is worth in the unit he is buying:
          investor reachouts. Both figures come from the stone; the reachout count is derived there. */}
      <p className="mt-4 text-sm text-slate-700 dark:text-slate-300">
        <strong>{formatCredits(plan.creditsPerMonth)} credits</strong> — the platform's currency, spendable
        on anything here, at{' '}
        <strong>{formatCredits(plan.creditsPerReachout)} credits a reachout</strong>.
      </p>
      <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
        About <strong>{plan.reachoutsPerMonth.toLocaleString('en-IE')} investor reachouts</strong> a
        month, each one written for that investor and sent for you.
      </p>

      {/* THE ESTIMATES — `est.` travels with each, exactly as the in-app dialog labels them. */}
      <dl className="mt-5 grid grid-cols-3 gap-2 rounded-xl bg-slate-50 p-3 text-center dark:bg-slate-800/60">
        <div>
          <dt className="text-xs text-slate-500 dark:text-slate-400">est. replies</dt>
          <dd className="text-base font-semibold text-slate-900 dark:text-white">{replies}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500 dark:text-slate-400">est. meetings</dt>
          <dd className="text-base font-semibold text-slate-900 dark:text-white">{meetings}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500 dark:text-slate-400">est. cost / meeting</dt>
          <dd className="text-base font-semibold text-slate-900 dark:text-white">{usd(perMeeting)}</dd>
        </div>
      </dl>

      <ul className="mt-5 space-y-2">
        {plan.features.map((feature) => (
          <li key={feature} className="flex gap-2 text-sm text-slate-700 dark:text-slate-300">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-600" aria-hidden />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 flex-1" />
      <Button
        className="w-full bg-cyan-600 hover:bg-cyan-700"
        onClick={() => onPress(plan)}
      >
        {chosen ? 'Continue to payment' : `Choose ${plan.name}`}
      </Button>
      {notice && (
        <p className="mt-2 text-xs text-[#0AB3BF]" role="status">
          {notice}
        </p>
      )}
    </div>
  );
};

export const PricingPage = () => {
  const navigate = useNavigate();
  const [chosen, setChosen] = useState<string | null>(chosenPlanKey);
  const [notice, setNotice] = useState<string | null>(null);
  const signedIn = hasSession();

  useSEO({
    title: 'Pricing - Raisy',
    description:
      'Three simple plans for running your raise, month to month: what each costs, what it buys in investor reachouts, and the range our published research estimates it produces.',
    keywords: 'raisy pricing, investor outreach pricing, fundraising outreach cost, per reachout pricing',
    ogTitle: 'Raisy pricing',
    ogDescription: 'What each plan costs and what it is estimated to produce.',
    ogType: 'website',
    canonicalUrl: 'https://raisy.team/pricing',
  });

  const press = (plan: Plan) => {
    setNotice(null);
    setChosen(plan.key);
    posthogCapture('pricing_cta', { tier: plan.key, signed_in: signedIn });

    // NO IDENTITY, NO CHECKOUT (D-083). The plan is carried through sign-up so he returns here with
    // the same plan selected rather than to a default surface with the choice lost.
    if (!signedIn) {
      rememberPostLoginPath(`/pricing?plan=${plan.key}`);
      navigate('/login');
      return;
    }

    const url = stripeCheckoutUrl(plan.stripeUrl);
    if (!url) {
      setNotice(
        "This plan's checkout link is not switched on yet, so nothing opened and nothing was charged. Tell us and we will send it to you directly.",
      );
      return;
    }
    posthogCapture('checkout_open', { rail: 'plan', tier: plan.key });
    void handToStripe(async () => url);
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
        <div className="mb-12 text-center">
          <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-100 dark:bg-cyan-900/30 dark:text-cyan-400">
            Pricing
          </Badge>
          <h1 className="text-4xl font-bold text-slate-900 md:text-5xl dark:text-white">
            The month that runs your raise
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-xl text-slate-600 dark:text-slate-300">
            Three plans, month to month, cancelled through Stripe whenever you like. Each one buys
            credits — one currency for everything here — and we write and send the investor outreach
            they pay for.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {PLANS.map((plan) => (
            <PlanCard
              key={plan.key}
              plan={plan}
              chosen={chosen === plan.key}
              notice={chosen === plan.key ? notice : null}
              onPress={press}
            />
          ))}
        </div>

        {/* THE HONEST UNDER-LINE — the numbers are estimates, the guarantee is not a meeting, and the
            free start costs nothing to try. No number is restated here that `plans.ts` owns. */}
        <p className="mt-8 text-center text-xs text-slate-500 dark:text-slate-400">
          <strong>Per reachout, not per meeting</strong> — one costs{' '}
          {PLANS.map((p) => formatCredits(p.creditsPerReachout)).join(' / ')} credits as the plan rises,
          spendable on anything here. Figures marked <strong>est.</strong> come from published
          cold-outreach research (6.5% reply, 40% of replies convert); we guarantee a response, not a
          meeting. Warm paths are always tried first — a cold reachout is what happens when none exists.
        </p>
        <p className="mt-3 text-center text-sm text-slate-600 dark:text-slate-300">
          Not ready to pay?{' '}
          <Link to="/blog" className="text-cyan-700 underline dark:text-cyan-400">
            Start free, without a card
          </Link>{' '}
          — the published table and the free plan are written out in full in our launch post.
        </p>
        {!paymentLinksReady && (
          <p className="mt-3 text-center text-xs text-[#0AB3BF]">
            Checkout links are being set up. Nothing here charges you yet.
          </p>
        )}
      </div>
    </PublicLayout>
  );
};
